/**
 * Login Service Module
 * Replaces: Login logic in AddinModule.cs
 * Original: AddinModule.cs adxRButtonLogin_OnClick() method (lines 380-520)
 */

import { tokenStorage } from './tokenStorage';
import { clearIdleSession, getIdleForMs, isIdleExpired, markOtpSession } from './idleSession';
import { OTP_IDLE_TIMEOUT_MS } from '../../utils/config';
import { clearPendingVerification } from './pendingVerification';
import { flushLogsNow, logAction } from '../../utils/actionLog';
import { writeProbeToken } from '../../store';
import { api } from '../api/endpoints';
import { HttpError } from '../api/httpClient';
import { createLogger } from '../../utils/logger';
import { config } from '../../utils/config';

const logger = createLogger('LoginService');

// Get add-in version from manifest or package
// Must match <Version> in manifest.xml / prod.manifest.xml (enforced by
// src/__tests__/unit/addin-version.test.ts) — it is sent as VersionNo on login.
export const ADDIN_VERSION = '1.1.0'; // TODO: Get from manifest.xml or package.json

/**
 * Which istari host the current session was authenticated against.
 *
 * Session-scoped and deliberately not persisted: it only matters between a
 * login and its matching logout, and it defaults to the normal host so a
 * reload after a dev login logs out against istari — which is the safe way to
 * be wrong, since that is where every real session lives.
 */
let loginHost: 'istari' | 'istari-new' = 'istari';

/**
 * Generate a machine ID for tracking
 * In Office.js, we can't get MAC address, so we generate a unique ID
 */

async function getMachineId(): Promise<string> {
  logger.enter('getMachineId');
  try {
    let machineId = await OfficeRuntime.storage.getItem('machineId');
    if (!machineId) {
      // Generate a random ID if not exists
      machineId = 'WEB-' + Math.random().toString(36).substring(2, 15);
      logger.debug('Generated new machine ID', { machineId });
      await OfficeRuntime.storage.setItem('machineId', machineId);
    } else {
      logger.debug('Retrieved existing machine ID', { machineId });
    }
    logger.exit('getMachineId', { machineId });
    return machineId;
  } catch (error) {
    logger.error('Failed to get machine ID', error as Error);
    throw error;
  }
}

/**
 * Get machine name from Office context
 */
function getMachineName(): string {
  // In Office.js, we have limited access to machine info
  // Use platform info if available, otherwise use a default
  const platform = Office.context?.platform?.toString() || 'Web';
  return `Excel-${platform}-${navigator.userAgent.split(' ')[0]}`;
}

export interface LoginCredentials {
  username: string;
  password: string;
  // Emailed OTP for accounts the server flags with `is_verify`. Sent as
  // `VerificationCode` on the same addin/login/ call.
  verificationCode?: string;
  /**
   * Authenticate against the istarinew host rather than the default one.
   *
   * Dev-only: the login pane offers this to an internal user when
   * IS_DEVELOPING is set. Everything else about the flow is unchanged — same
   * body, same response parsing, same token storage — so it exists purely to
   * exercise the new backend before it becomes the default.
   */
  useIstariNew?: boolean;
}

export interface LoginResult {
  success: boolean;
  token?: string;
  fullName?: string;
  isDeveloper?: boolean;
  deviceStatus?: string;
  clientInternalID?: string;
  rmsId?: string;
  userId?: string;
  fullAccess?: string;
  valuationVisible?: string;
  message?: string;
  error?: string;
  // True when the server answered `{ is_verify: true }`: a code was emailed and
  // login must be repeated with `verificationCode`. Nothing is stored yet.
  requiresVerification?: boolean;
}

// The server sends these flags as JSON booleans, but accept the Python-style
// "True" string too in case it is ever serialised that way.
function isTrueFlag(value: unknown): boolean {
  return value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');
}

function getUserFriendlyLoginError(error: unknown, isVerificationStep = false): string {
  if (error instanceof HttpError) {
    // On the OTP step the credentials were already accepted once, so a 400/401
    // means the code itself was rejected (wrong or expired).
    if (isVerificationStep && (error.status === 400 || error.status === 401)) {
      return 'The verification code is invalid or has expired. Please check the code or resend a new one.';
    }
    switch (error.status) {
      case 400:
        return 'We could not sign you in. Please check your login details and try again.';
      case 401:
        return 'Invalid username or password. Please check your credentials and try again.';
      case 403:
        return 'Your account does not have access to this add-in. Please contact support.';
      case 429:
        return 'Too many login attempts. Please wait a few minutes and try again.';
      default:
        if (error.status >= 500) {
          return 'The login service is temporarily unavailable. Please try again in a few minutes.';
        }

        return 'We could not sign you in. Please try again or contact support.';
    }
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'The login request timed out. Please check your internet connection and try again.';
  }

  const message = error instanceof Error ? error.message : '';

  if (/timeout/i.test(message)) {
    return 'The login request timed out. Please check your internet connection and try again.';
  }

  if (/failed to fetch|network/i.test(message)) {
    return 'Unable to reach the login service. Please check your internet connection and try again.';
  }

  return 'We could not sign you in right now. Please try again in a few minutes.';
}

const USE_DIRECT_ENCRYPT = false; // direct is CORS-blocked from the browser — proxy is mandatory

async function fetchUserTempToken(username: string, password: string): Promise<string | null> {
  if (!username || !password) return null;
  try {
    const response = await api.legacy.encryptUserToken(username, password, USE_DIRECT_ENCRYPT);
    const data = response.data;
    // Valid response is the encrypted token as a JSON string, e.g.
    // "ascascscsdc==". 
    const token = typeof data === 'string' ? data.trim() : '';
    if (!token || !/^[A-Za-z0-9+/=_.-]+$/.test(token)) {
      logger.warn('Per-user temp token response was not a valid token string', {
        username,
        status: response.status,
        sample: JSON.stringify(data ?? null).slice(0, 80),
        response:response,
        USE_DIRECT_ENCRYPTL:USE_DIRECT_ENCRYPT
      });
      return null;
    }
    return token;
  } catch (error) {
    logger.warn('Per-user temp token fetch error (non-blocking)', { message: (error as Error)?.message });
    return null;
  }
}

/**
 * Login Service
 * Handles authentication flow and session management
 */
export class LoginService {
  // Per-user encrypt token fetched on the first (password) step of an OTP
  // login, reused by the verify step so the encrypt call isn't repeated.
  // Held in memory only and dropped once the login completes.
  private pendingTempToken: { username: string; promise: Promise<string | null> } | null = null;
  // De-dupes the idle logout when the watcher and a route guard fire together.
  private idleLogoutInFlight: Promise<boolean> | null = null;
  private revokedLogoutInFlight: Promise<void> | null = null;

  /**
   * Attempt to login with credentials
   * Equivalent to: AddinModule.cs adxRButtonLogin_OnClick() login flow
   */
  async login(credentials: LoginCredentials): Promise<LoginResult> {
    logger.enter('login', { username: credentials.username });
    
    try {
      // Validate input
      if (!credentials.username || !credentials.password) {
        logger.warn('Login failed: Missing credentials');
        return { 
          success: false,
          error: 'Invalid login details - username and password required' 
        };
      }

      // Fire the per-user encrypt call in PARALLEL with the login API below — it
      // only needs the typed username (+ the service password), available now.
      // Committed to storage only AFTER login succeeds (discarded on failure).
      const trimmedUsername = credentials?.username?.trim() ?? '';
      const verificationCode = credentials.verificationCode?.trim() || undefined;
      const reusablePending = verificationCode && this.pendingTempToken?.username === trimmedUsername
        ? this.pendingTempToken.promise
        : null;
      const tempTokenPromise = reusablePending ?? fetchUserTempToken(
         trimmedUsername,
         (credentials?.password?.trim() ?? '')
      );

      // Get machine info for new API
      const sysname = getMachineName();
      const mcid = await getMachineId();
      // Debug, not info: it fires before the credentials are checked, so a
      // shipped copy would look like a login. Auth.LoggedIn carries these.
      logger.debug('Machine info collected', { sysname, mcid, version: ADDIN_VERSION });

      const useIstariNew = credentials.useIstariNew === true;
      // Remembered for the session so logout goes back to the same host — the
      // other one has no record of this device and the call would fail.
      loginHost = useIstariNew ? 'istari-new' : 'istari';

      const response = await api.legacy.validateLoginV2(
        credentials?.username?.trim(),
        credentials?.password?.trim(),
        sysname,
        mcid,
        ADDIN_VERSION,
        verificationCode,
        useIstariNew
      );

      logger.apiResponse('POST', 'addin/login/', response.status, {
        hasData: !!response.data,
        deviceStatus: response.data?.DeviceStatus,
        hasTokenKey: !!response.data?.TokenKey,
        hasTokenID: !!response.data?.TokenID,
        hasRMSID: !!response.data?.RMSID,
        fullName: response.data?.FullName
      });

      // Parse new API response
      // Response: { DeviceStatus, TokenKey, RMSID, FullName, ClientInternalID, ... }
      const deviceStatus = response.data?.DeviceStatus;
      const tokenKey = response.data?.TokenKey || response.data?.TokenID;
      const fullName = response.data?.FullName;
      const clientInternalID = response.data?.ClientInternalID;
      const fullAccess = response.data?.FullAccess;
      const userName = response.data?.UserName || credentials.username;
      const rmsId = response.data?.RMSID;
      const userId = response.data?.UserID;
      const valuationVisible = response.data?.ValuationVisible;
      const loginMessage = response.data?.message;
      const isVerify = isTrueFlag(response.data?.is_verify);


      // OTP accounts: the password was accepted and a code was emailed. Keep the
      // encrypt token in flight for the verify step; store nothing yet.
      if (isVerify && !verificationCode) {
        this.pendingTempToken = { username: trimmedUsername, promise: tempTokenPromise };
        logger.info('Login requires email verification', { username: credentials.username });
        logger.exit('login', { success: false, requiresVerification: true });
        return {
          success: false,
          requiresVerification: true,
          message: loginMessage?.toString(),
        };
      }

      // Check DeviceStatus
      // "Active" = success, "0" = inactive, "2" = max 5 devices reached
      if (deviceStatus === '0' || deviceStatus === 0) {
        logger.warn('Login failed: Account inactive', { deviceStatus, username: credentials.username });
        await tokenStorage.clearAuth();
        return { success: false, error: 'Account inactive. Please contact support.' };
      }
      
      if (deviceStatus === '2' || deviceStatus === 2) {
        logger.warn('Login failed: Maximum devices reached', { deviceStatus, username: credentials.username });
        await tokenStorage.clearAuth();
        return { success: false, error: 'Maximum 5 devices reached. Please contact support.' };
      }
      
      // OTP verify step: a success may or may not carry `is_login: true` — both
      // shapes are valid, and the TokenKey/DeviceStatus check below decides.
      // Only treat it as a rejected code when the server says so explicitly:
      // `is_login: false`, or it asks for verification again without a token.
      const isLoginField = response.data?.is_login;
      const loginExplicitlyRefused = isLoginField !== undefined && isLoginField !== null && !isTrueFlag(isLoginField);
      if (verificationCode && (loginExplicitlyRefused || (isVerify && !tokenKey))) {
        logger.warn('Verification failed: code rejected', { username: credentials.username, isVerify, isLogin: isLoginField });
        await tokenStorage.clearAuth();
        return {
          success: false,
          error: 'The verification code is invalid or has expired. Please check the code or resend a new one.',
        };
      }

      if (!tokenKey || tokenKey === '-Mac-') {
        // Fall back to legacy token format check
        if (!deviceStatus || deviceStatus !== 'Active') {
          logger.warn('Login failed: Invalid token or device status', { 
            deviceStatus, 
            tokenKey: tokenKey ? '***' : 'missing',
            username: credentials.username 
          });
          await tokenStorage.clearAuth();
          return { success: false, error: 'Wrong login' };
        }
      }

      // The current ISTARI and consensus endpoints expect the username_filter
      // token value. Use TokenKey directly until the server-side auth contract
      // is updated for Office.js.
      const token = tokenKey;
      
      // Parse legacy token string format if needed (format: "token-?-developerFlag")
      let isDeveloper = fullAccess?.toString() === '1';
      if (!isDeveloper && token && token.includes('-')) {
        const parts = token.split('-');
        isDeveloper = parts[2]?.trim() === '1';
        logger.info('Parsed developer flag from legacy token', { isDeveloper });
      }

      // Store auth data with FullName
      // logger.info('Storing auth data', { 
      //   username: userName, 
      //   fullName, 
      //   isDeveloper,
      //   hasToken: !!token,
      //   tokenSource: 'token-key'
      // });
      
      await tokenStorage.setAuthToken(token, userName, fullName, clientInternalID, {
        rmsId: rmsId?.toString(),
        userId: userId?.toString(),
        deviceStatus: deviceStatus?.toString(),
        fullAccess: fullAccess?.toString(),
        valuationVisible: valuationVisible?.toString(),
        message: loginMessage?.toString(),
      });
      await tokenStorage.setDeveloperMode(isDeveloper);
      // VAPT V-001: the edge-auth token IS the istari login token — tokenStorage
      // .setAuthToken() (above) already stored + mirrored it as the edge token,
      // which the client presents as X-Insync-Token on data calls. No separate
      // mint step; existing users migrate without re-login (see getEdgeToken).

      // Login succeeded — commit the encrypt token fetched in PARALLEL above,
      // deterministically, BEFORE returning, so the first data formula/batch after
      // login already sees it (custom functions read it fresh from storage). Bounded
      // so a slow encrypt never blocks login; the TEMP_TOKEN bridge covers the gap
      // if it times out or the encrypt call failed/returned [].
      try {
        const tempToken = await Promise.race([
          tempTokenPromise,
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
        ]);
        if (tempToken) {
          await tokenStorage.setUserTempToken(tempToken);
          // logger.info('Stored per-user temp token', { length: tempToken.length });
          // DIAG: read the token straight back from OfficeRuntime.storage in the
          // taskpane runtime. If this shows the token but the custom-functions
          // AUTHCHECK shows rawUser=absent, OfficeRuntime.storage is not sharing
          // this write across runtimes (vs. it never landing here at all).
          try {
            const rawStore = (window as any).OfficeRuntime?.storage;
            const backend = tokenStorage.getStorageBackendName();
            const rawUser = rawStore ? await rawStore.getItem('iad_user_temp_token') : 'NO_RT';
            const rawAuth = rawStore ? await rawStore.getItem('iad_auth_token') : 'NO_RT';
            // logger.warn('[DIAG] taskpane storage after commit', {
            //   backend,
            //   rawUserLen: rawUser ? String(rawUser).length : 0,
            //   rawUserPrefix: rawUser ? String(rawUser).slice(0, 4) : 'null',
            //   rawAuthLen: rawAuth ? String(rawAuth).length : 0,
            // });
          } catch (diagErr) {
            logger.warn('[DIAG] taskpane raw read failed', diagErr as Error);
          }
        } else {
          logger.warn('Per-user temp token not committed at login (timeout/empty); using TEMP_TOKEN bridge');
        }
      } catch (tokenErr) {
        logger.warn('Per-user temp token commit failed; using TEMP_TOKEN bridge', tokenErr as Error);
      }

      // DIAG probe: write a known value via the BARE-global OfficeRuntime.storage
      // (and localStorage) from the taskpane, so IAD AUTHCHECK can test whether the
      // custom-functions runtime sees it. Isolates cross-runtime sharing from the
      // tokenStorage module. Remove once the storage question is settled.
      try {
        await writeProbeToken('TASKPANE');
        // logger.warn('[DIAG] wrote cross-runtime probe (office+localStorage)');
      } catch { /* best-effort diagnostic */ }

      this.pendingTempToken = null;

      // Only OTP-verified sessions get the idle timeout; a normal login also
      // clears any idle state left over from a previous OTP user.
      if (verificationCode) {
        markOtpSession();
      } else {
        clearIdleSession();
      }

      logger.debug('Login successful', {
        username: userName,
        fullName,
        deviceStatus,
        // OTP-verified sessions are the ones subject to the idle timeout.
        otpVerified: !!verificationCode,
      });

      logger.exit('login', { success: true, fullName, isDeveloper });

      // Fills the gap nginx cannot: its /addin/login row has an empty
      // user_email because credentials are in the unlogged POST body.
      // The one shipped record of a successful login (activity LoginSucceeded,
      // same as the COM add-in), carrying every Add-In Login column.
      logAction('Auth.LoggedIn', 'Login successful', {
        deviceStatus: deviceStatus?.toString(),
        isDeveloper,
        userId: userId?.toString(),
        userName,
        fullName,
        clientInternalId: clientInternalID?.toString(),
        mcid,
        sysname,
        version: ADDIN_VERSION,
        // OTP-verified sessions are the ones subject to the idle timeout.
        otpVerified: !!verificationCode,
      });

      return {
        success: true,
        token,
        fullName,
        isDeveloper,
        deviceStatus: deviceStatus?.toString(),
        clientInternalID,
        rmsId: rmsId?.toString(),
        userId: userId?.toString(),
        fullAccess: fullAccess?.toString(),
        valuationVisible: valuationVisible?.toString(),
        message: loginMessage?.toString(),
      };

    } catch (error) {
      // Clear auth on error
      logger.error('Login error occurred', error as Error, {
        username: credentials.username,
        verificationStep: !!credentials.verificationCode?.trim(),
        status: error instanceof HttpError ? error.status : undefined,
      });
      await tokenStorage.clearAuth();

      return {
        success: false,
        error: getUserFriendlyLoginError(error, !!credentials.verificationCode?.trim())
      };
    }
  }

  /**
   * Logout current user
   * Equivalent to: ADXExcelTaskPane1.cs btnLogout_Click() (lines 575-657)
   *
   * Calls the server `addin/logout/` endpoint with the machine id + token so the
   * session/device is invalidated server-side, then clears the local session.
   * The local session is only cleared when the server call succeeds (mirrors the
   * legacy "CleanReg() on IsSuccessStatusCode" behaviour), so a failed logout can
   * be retried from the Logout pane.
   */
  async logout(): Promise<void> {
    logger.enter('logout');
    // Never resume a half-finished OTP login after logging out.
    clearPendingVerification();

    const token = await tokenStorage.getAuthToken();

    // No active token means there is nothing to invalidate server-side; just
    // make sure local state is clear.
    if (!token) {
      // Logged and flushed BEFORE clearAuth: that call drops the cached
      // username, and user_email is backfilled from it at flush time - so
      // clearing first would strip the identity off this record and every other
      // record still waiting from this session.
      logAction('Auth.LoggedOut', 'Logged out (no active token)', { serverCall: false });
      flushLogsNow();
      await tokenStorage.clearAuth();
      logger.exit('logout', { serverCall: false });
      return;
    }
    logAction('Auth.LoggedOut', 'Logged out', { serverCall: true });
    // Flushed here, before the server logout and the clearAuth that follows it,
    // for the same reason: after clearAuth there is no identity left to attach.
    flushLogsNow();
    try{
      const mcid = await getMachineId();
      logger.apiRequest('POST', 'addin/logout/', { mcid, hasToken: true });
      const response = await api.legacy.logoutV2(token, mcid, loginHost === 'istari-new');
      await tokenStorage.clearAuth();
      loginHost = 'istari';
      logger.apiResponse('POST', 'addin/logout/', response.status, { hasData: !!response.data });
    }
    catch (error) {
      // The local session is cleared regardless, but the server may still
      // consider this device logged in (e.g. the next OTP login then skips the
      // code) — make that visible.
      logger.warn('Logout API failed; cleared local session only', {
        username: await tokenStorage.ensureUsername(),
        status: error instanceof HttpError ? error.status : undefined,
        message: (error as Error)?.message,
      });
      await tokenStorage.clearAuth();
    }
    logger.exit('logout', { serverCall: true });
  }

  /**
   * Log out an OTP session that has been idle past OTP_IDLE_TIMEOUT_MS. Calls
   * the server logout (like the Logout button) and clears the local session.
   * Returns true when it logged the user out.
   */
  /**
   * The server ended this session (nginx answered `X-Insync-Session: session`,
   * see utils/sessionEnded): clear the local session so the app stops sending a
   * dead token. No server logout call - the session is already gone there.
   * Several requests usually fail at once, so concurrent calls share one run.
   */
  async endRevokedSession(): Promise<void> {
    if (this.revokedLogoutInFlight) return this.revokedLogoutInFlight;
    this.revokedLogoutInFlight = (async () => {
      try {
        clearPendingVerification();
        logAction('Auth.SessionEnded', 'Session ended server-side', { serverCall: false });
        flushLogsNow();
        await tokenStorage.clearAuth();
        loginHost = 'istari';
      } catch (error) {
        logger.warn('Clearing an ended session failed', error as Error);
      }
    })();
    try {
      await this.revokedLogoutInFlight;
    } finally {
      this.revokedLogoutInFlight = null;
    }
  }

  async enforceIdleTimeout(): Promise<boolean> {
    if (this.idleLogoutInFlight) return this.idleLogoutInFlight;
    if (!isIdleExpired()) return false;

    this.idleLogoutInFlight = (async () => {
      logger.info('Logging out OTP session after inactivity', {
        username: await tokenStorage.ensureUsername(),
        idleForMs: getIdleForMs(),
        timeoutMs: OTP_IDLE_TIMEOUT_MS,
      });
      try {
        await this.logout();
      } catch (error) {
        logger.warn('Idle logout failed; clearing local session', error as Error);
        await tokenStorage.clearAuth();
      }
      return true;
    })();

    try {
      return await this.idleLogoutInFlight;
    } finally {
      this.idleLogoutInFlight = null;
    }
  }

  /**
   * Check if user is authenticated
   * Equivalent to: AddinModule.cs TokenID != "" checks throughout
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await tokenStorage.getAuthToken();
    return !!token;
  }

  /**
   * Attempt auto-login on startup
   * Equivalent to: AddinModule.cs StartupCall() method (lines 174-377)
   */
  // async attemptAutoLogin(): Promise<LoginResult> {
  //   // Check if session is valid (3-hour timeout)
  //   const isValid = await tokenStorage.isSessionValid();
    
  //   if (!isValid) {
  //     return { success: false, error: 'Session expired' };
  //   }
  //   // Get stored credentials
  //   const token = await tokenStorage.getAuthToken();
  //   const isDeveloper = await tokenStorage.isDeveloperMode();

  //   if (!token) {
  //     return { success: false, error: 'No stored credentials' };
  //   }

  //   return {
  //     success: true,
  //     token,
  //     isDeveloper
  //   };
  // }

  /**
   * Get current auth state for UI
   */
  async getAuthState(): Promise<{
    isAuthenticated: boolean;
    username: string | null;
    isDeveloper: boolean;
    sessionValid: boolean;
    loggedOutForInactivity: boolean;
  }> {
    const loggedOutForInactivity = await this.enforceIdleTimeout();
    const [authenticated, username, developer, valid] = await Promise.all([
      this.isAuthenticated(),
      tokenStorage.getUsername(),
      tokenStorage.isDeveloperMode(),
      tokenStorage.isSessionValid()
    ]);

    return {
      isAuthenticated: authenticated,
      username,
      isDeveloper: developer,
      sessionValid: valid,
      loggedOutForInactivity,
    };
  }
}

// Singleton instance
export const loginService = new LoginService();

export default loginService;
